import { computed } from 'vue'
import portionsData from '@/data/portions.json'
import { useCollection } from './useDatastore'
import {
	parashahSlugIndex,
	portionOrderKeys,
	type BookRow,
	type ParashahSlugRow,
} from '@/utils/parashahPath'

/** Portion → position key, built once from the local reading order. */
const ORDER_KEY_BY_NAME = portionOrderKeys(portionsData)

/**
 * Resolve a Torah portion to its published `/parashah/:slug`.
 *
 * The portion of the week comes from the Hebrew calendar and is matched against
 * `portions.json`, but the URL segment belongs to the `parashahs` collection —
 * and the two spell things differently (`Chayei Sarah` → `chayei-sara`,
 * `Shemini` → `shmini`, `Shelach` → `shlach`, `Behaalotecha` →
 * `behaalotcha`). Building the link from the display name therefore 404s the
 * portion of the week on the homepage and in the book index, for whichever
 * portion happens to differ.
 *
 * Portions are matched by their position in their book instead of their name,
 * so a link always points at the published slug. A portion with no published
 * record has no link until that slug arrives.
 *
 * Both collections are requested with one shared key, so nav, widget and book
 * blocks reuse a single fetch, and the SSG payload carries it once per page.
 */
export function useParashahSlugs() {
	const { items: parashahs, loading: parashahsLoading } = useCollection<ParashahSlugRow>('parashahs', {
		limit: 200,
		pick: ['slug', 'bookId', 'orderInBook'],
	})
	const { items: books, loading: booksLoading } = useCollection<BookRow>('books', {
		limit: 50,
		pick: ['name'],
	})

	const slugByOrder = computed(() => parashahSlugIndex(parashahs.value, books.value))

	/** `/parashah/:slug` for a portion from `portions.json`. */
	function pathFor(name: string | undefined | null): string {
		if (!name) return ''
		const key = ORDER_KEY_BY_NAME.get(name)
		const slug = key ? slugByOrder.value.get(key) : undefined
		return slug ? `/parashah/${slug}` : ''
	}

	return {
		pathFor,
		loading: computed(() => parashahsLoading.value || booksLoading.value),
	}
}
